import { useEffect, useState } from "react";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Ban, RotateCcw } from "lucide-react";

// Category Master — seeded launch categories (Site Running Expense, Vendor
// Bill Payment, Salary or Staff Payment) plus any added later. All four
// Payment Desk roles may add/rename/deactivate — no restriction, per spec.
export default function CategoryMasterTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [newName, setNewName] = useState("");
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    client
      .get("/payment-desk/categories", { params: { include_inactive: includeInactive } })
      .then((res) => setItems(res.data))
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [includeInactive]);

  const addItem = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return toast.error("Category Name is required");
    setBusy(true);
    try {
      await client.post("/payment-desk/categories", { name: newName.trim() });
      toast.success("Category added");
      setNewName("");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const rename = async (id) => {
    if (!editName.trim()) return toast.error("Category Name cannot be empty");
    setBusy(true);
    try {
      await client.put(`/payment-desk/categories/${id}`, { name: editName.trim() });
      toast.success("Category renamed");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (it) => {
    setBusy(true);
    try {
      await client.post(`/payment-desk/categories/${it.id}/${it.active ? "deactivate" : "reactivate"}`);
      toast.success(it.active ? "Category deactivated" : "Category reactivated");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="category-master-tab">
      <form onSubmit={addItem} className="flex gap-2 max-w-md">
        <Input data-testid="category-new-input" placeholder="New Category Name" value={newName} onChange={(e) => setNewName(e.target.value)} />
        <Button type="submit" disabled={busy} data-testid="category-add-button" className="bg-amber-600 hover:bg-amber-700">
          <Plus className="h-4 w-4 mr-1.5" /> Add
        </Button>
      </form>

      <button
        data-testid="category-include-inactive-toggle"
        onClick={() => setIncludeInactive((v) => !v)}
        className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
          includeInactive ? "bg-amber-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
        }`}
      >
        {includeInactive ? "Showing All" : "Showing Active Only"}
      </button>

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <Table data-testid="category-table">
          <TableHeader>
            <TableRow>
              <TableHead>Category Name</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={3} className="text-center py-8 text-slate-400">Loading...</TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} className="text-center py-8 text-slate-400">No categories found.</TableCell>
              </TableRow>
            ) : (
              items.map((it) => (
                <TableRow key={it.id} data-testid={`category-row-${it.id}`}>
                  <TableCell>
                    {editing === it.id ? (
                      <Input className="h-8" data-testid={`category-edit-input-${it.id}`} value={editName} onChange={(e) => setEditName(e.target.value)} />
                    ) : (
                      <span className="font-medium">{it.name}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge className={it.active === false ? "bg-slate-400" : "bg-emerald-600"}>{it.active === false ? "Inactive" : "Active"}</Badge>
                  </TableCell>
                  <TableCell className="text-right space-x-2">
                    {editing === it.id ? (
                      <>
                        <Button size="sm" disabled={busy} data-testid={`category-save-${it.id}`} onClick={() => rename(it.id)} className="bg-amber-600 hover:bg-amber-700">
                          Save
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                      </>
                    ) : (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`category-edit-${it.id}`}
                          onClick={() => {
                            setEditing(it.id);
                            setEditName(it.name);
                          }}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant={it.active === false ? "outline" : "destructive"}
                          disabled={busy}
                          data-testid={`category-toggle-active-${it.id}`}
                          onClick={() => toggleActive(it)}
                        >
                          {it.active === false ? <RotateCcw className="h-3.5 w-3.5" /> : <Ban className="h-3.5 w-3.5" />}
                        </Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
